import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StatsWidget from './StatsWidget';
import DonutChart from '../../charts/DonutChart';
import LeaderboardWidget from './LeaderboardWidget';
import AgentAvailabilityWidget from './AgentAvailabilityWidget';
import SlaMetricsWidget from './SlaMetricsWidget';
import PendingApprovalsWidget from './PendingApprovalsWidget';
import Icon from '../../ui/Icon';
import { T } from '../../constants/mockData';
import RecentActivityModal from '../../modals/RecentActivityModal';
import AnnouncementsModal from '../../modals/AnnouncementsModal';
import { dashboardAPI } from '../../services/api';
import { formatInTz } from '../../utils/timezone';

const WIDGET_REGISTRY = [
  { id: 'stats',        label: 'Key Stats',              icon: '📊', desc: 'Overdue, open, unassigned counts',    defaultVisible: true,  span: 'full' },
  { id: 'priority',     label: 'Tickets by Priority',    icon: '🟣', desc: 'Donut chart of open ticket priority', defaultVisible: true,  span: 1 },
  { id: 'assigned',     label: 'Tickets by Assignee',    icon: '👤', desc: 'Bar chart of tickets per agent',      defaultVisible: true,  span: 1 },
  { id: 'sla',          label: 'SLA Metrics',            icon: '⏱',  desc: 'SLA compliance rate & breakdown',     defaultVisible: true,  span: 1 },
  { id: 'leaderboard',  label: 'Agent Leaderboard',      icon: '🏆', desc: 'Top resolving agents',                defaultVisible: true,  span: 1 },
  { id: 'availability', label: 'Agent Availability',     icon: '🟢', desc: 'Online/offline agent status',         defaultVisible: true,  span: 1 },
  { id: 'approvals',    label: 'Pending Notifications',  icon: '🔔', desc: 'Recent ticket notifications',         defaultVisible: true,  span: 1 },
  { id: 'trend',        label: 'Ticket Trend',           icon: '📈', desc: 'Daily ticket volume (30 days)',       defaultVisible: true,  span: 'full' },
];

const STORAGE_KEY = 'dashboard_widget_config';

function fmtDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? String(d) : formatInTz(dt, { day: '2-digit', month: 'short' });
  } catch {
    return String(d);
  }
}

function loadWidgetConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return WIDGET_REGISTRY.map(w => ({ id: w.id, visible: w.defaultVisible, order: WIDGET_REGISTRY.indexOf(w) }));
}

function saveWidgetConfig(config) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); } catch {}
}

// ─── Mini trend sparkline ─────────────────────────────────────────────────────
function TrendSparkline({ data = [] }) {
  if (!data.length) return <div style={{ textAlign: 'center', padding: 24, color: '#718096', fontSize: 13 }}>No trend data</div>;
  const vals  = data.map(d => Number(d.count || d.created) || 0);
  const max   = Math.max(...vals, 1);
  const w = 600, h = 100;
  const pts = vals.map((v, i) => {
    const x = (i / Math.max(vals.length - 1, 1)) * (w - 20) + 10;
    const y = h - 10 - (v / max) * (h - 20);
    return `${x},${y}`;
  }).join(' ');
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 100 }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B5BDB" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#3B5BDB" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon fill="url(#sparkGrad)" points={`10,${h} ${pts} ${w-10},${h}`} />
        <polyline fill="none" stroke="#3B5BDB" strokeWidth="2.5" points={pts} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#718096', marginTop: 4 }}>
        <span>{fmtDate(data[0]?.date)}</span>
        <span>{fmtDate(data[Math.floor(data.length / 2)]?.date)}</span>
        <span>{fmtDate(data[data.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

// ─── Widget customize panel ───────────────────────────────────────────────────
function WidgetPanel({ config, onChange, onClose }) {
  const [local, setLocal] = useState(config);

  const toggle = (id) => setLocal(c => c.map(w => w.id === id ? { ...w, visible: !w.visible } : w));
  const move   = (id, dir) => {
    const idx = local.findIndex(w => w.id === id);
    if (idx + dir < 0 || idx + dir >= local.length) return;
    const next = [...local];
    [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
    setLocal(next);
  };

  return (
    <div style={{
      position: 'absolute', top: 0, right: 0, bottom: 0, width: 320, zIndex: 50,
      background: '#FFFFFF', borderLeft: '1px solid #DDE1E7',
      boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid #DDE1E7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1A202C' }}>Customise Dashboard</div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>Show, hide and reorder widgets</div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#718096', fontSize: 18 }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {local.map((cfg, i) => {
          const meta = WIDGET_REGISTRY.find(w => w.id === cfg.id);
          if (!meta) return null;
          return (
            <div key={cfg.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px',
              borderBottom: '1px solid #F0F2F5', background: cfg.visible ? '#FFFFFF' : '#FAFAFA',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{meta.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: cfg.visible ? '#1A202C' : '#718096' }}>{meta.label}</div>
                <div style={{ fontSize: 11, color: '#718096' }}>{meta.desc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => move(cfg.id, -1)} disabled={i === 0} style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#DDE1E7' : '#718096', fontSize: 14 }}>↑</button>
                <button onClick={() => move(cfg.id, +1)} disabled={i === local.length - 1} style={{ border: 'none', background: 'none', cursor: i === local.length - 1 ? 'default' : 'pointer', color: i === local.length - 1 ? '#DDE1E7' : '#718096', fontSize: 14 }}>↓</button>
                {/* Toggle switch */}
                <div onClick={() => toggle(cfg.id)} style={{
                  width: 38, height: 20, borderRadius: 99, cursor: 'pointer',
                  background: cfg.visible ? '#3B5BDB' : '#C8CDD6',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <div style={{
                    position: 'absolute', top: 2, left: cfg.visible ? 18 : 2, width: 16, height: 16,
                    borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: '16px 20px', borderTop: '1px solid #DDE1E7', display: 'flex', gap: 10 }}>
        <button
          onClick={() => { onChange(local); saveWidgetConfig(local); onClose(); }}
          style={{ flex: 1, padding: '10px', background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px var(--accent-bg)' }}
        >
          Save Layout
        </button>
        <button
          onClick={() => {
            const def = WIDGET_REGISTRY.map(w => ({ id: w.id, visible: w.defaultVisible, order: WIDGET_REGISTRY.indexOf(w) }));
            setLocal(def);
          }}
          style={{ padding: '10px 14px', background: '#F0F2F5', color: '#718096', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

// ─── Dashboard card wrapper ───────────────────────────────────────────────────
function DCard({ title, children, noPadding = false, onRemove, editMode, onClick, hint }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? hint : undefined}
      style={{
        background: '#FFFFFF', border: '1px solid #DDE1E7', borderRadius: 14,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'relative', height: '100%',
        cursor: clickable ? 'pointer' : 'default',
        transition: clickable ? 'box-shadow 0.15s' : undefined,
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,91,219,0.12)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; } : undefined}
    >
      {editMode && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(59,91,219,0.04)', border: '2px dashed #3B5BDB', borderRadius: 14, zIndex: 10, pointerEvents: 'none' }} />
      )}
      {title && (
        <div style={{ padding: '14px 18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1A202C' }}>{title}</div>
          {editMode && <button onClick={onRemove} style={{ border: 'none', background: '#FEF2F2', color: '#dc2626', borderRadius: 6, cursor: 'pointer', padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>Remove</button>}
        </div>
      )}
      <div style={{ padding: noPadding ? 0 : '14px 18px', flex: 1 }}>{children}</div>
    </div>
  );
}

// ─── Main Dashboard Page ──────────────────────────────────────────────────────
export default function DashboardPage({ tickets = [] }) {
  const navigate = useNavigate();
  const goTickets = (params) => navigate(`/tickets?${new URLSearchParams(params).toString()}`);

  const [widgetConfig, setWidgetConfig] = useState(loadWidgetConfig);
  const [showCustomise, setShowCustomise] = useState(false);
  const [editMode, setEditMode]           = useState(false);
  const [showRecentActivity, setShowRecentActivity] = useState(false);
  const [showAnnouncements, setShowAnnouncements]   = useState(false);

  const [stats, setStats]           = useState({ overdue: 0, dueToday: 0, open: 0, unassigned: 0, avgResolution: '—', avgFirstResponse: '—' });
  const [priorityData, setPriority] = useState([]);
  const [assignedData, setAssigned] = useState([]);
  const [agentData, setAgents]      = useState([]);
  const [approvalsData, setApprovals] = useState([]);
  const [trendData, setTrend]       = useState([]);
  const [slaData, setSla]           = useState({ complianceRate: 0 });

  const loadAgents = React.useCallback(() => {
    dashboardAPI.leaderboard().then(rows => {
      const list = (rows || []).map(r => ({
        label:      r.name || '',
        value:      Number(r.resolved_count || 0),
        total:      Number(r.total_assigned || 0),
        open:       Number(r.open_count || 0),
        lastActive: r.last_active || null,
        isOnline:   r.is_online === true,
      }));
      setAgents(list);
      setAssigned(list.slice(0, 6));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    dashboardAPI.stats().then(d => setStats({
      overdue: d.overdue || 0, dueToday: d.due_today || 0,
      open: d.open || 0, unassigned: d.unassigned || 0,
      avgResolution: d.avg_resolution || '—', avgFirstResponse: d.avg_first_response || '—',
    })).catch(() => {});

    dashboardAPI.byPriority().then(rows => {
      const colorMap = { Low: '#718096', Medium: '#f97316', High: '#ef4444', Urgent: '#1d4ed8' };
      setPriority((rows || []).map(r => ({ name: r.priority, value: Number(r.count) || 0, color: colorMap[r.priority] || '#3B5BDB' })));
    }).catch(() => {});

    loadAgents();

    dashboardAPI.trend().then(rows => setTrend(rows || [])).catch(() => {});

    dashboardAPI.notifications().then(items => setApprovals(
      (items || []).slice(0, 5).map(n => ({
        id: n.ticket_number || `#${n.ticket_id}`,
        ticketNumber: n.ticket_number || '',
        ticketId: n.ticket_id,
        title: n.subject || n.message || '',
        requester: n.requester_name || '',
        time: n.created_at ? formatInTz(n.created_at, { day: '2-digit', month: 'short' }) : '',
      }))
    )).catch(() => {});

    // SLA summary
    import('../../services/api').then(({ reportsAPI }) => {
      const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const to   = new Date().toISOString().slice(0, 10);
      reportsAPI.summary({ from, to }).then(d => setSla({ complianceRate: Number(d.sla?.compliance_pct || 0) })).catch(() => {});
    }).catch(() => {});
  }, [loadAgents]);

  // Refresh agent availability periodically and when tab regains focus
  useEffect(() => {
    const id = setInterval(loadAgents, 60_000);
    const onFocus = () => loadAgents();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [loadAgents]);

  const visibleWidgets = widgetConfig.filter(w => w.visible);
  const removeWidget   = (id) => {
    const next = widgetConfig.map(w => w.id === id ? { ...w, visible: false } : w);
    setWidgetConfig(next); saveWidgetConfig(next);
  };

  function renderWidget(id) {
    switch (id) {
      case 'stats':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
            <StatsWidget title="Overdue Tickets" value={stats.overdue} type="alert" onClick={() => goTickets({ overdue: '1' })} hint="Tickets past due date" />
            <StatsWidget title="Due Today" value={stats.dueToday} type="alert" onClick={() => goTickets({ dueToday: '1' })} hint="Tickets due today" />
            <StatsWidget title="Open Tickets" value={stats.open} type="normal" onClick={() => goTickets({ status: 'Open' })} hint="Tickets with Open status" />
            <StatsWidget title="Unassigned" value={stats.unassigned} type="normal" onClick={() => goTickets({ unassigned: '1' })} hint="Tickets with no assignee" />
            <StatsWidget title="Avg Resolution" value={stats.avgResolution} type="normal" onClick={() => navigate('/reports')} hint="View reports" />
            <StatsWidget title="Avg First Response" value={stats.avgFirstResponse} type="normal" onClick={() => navigate('/reports')} hint="View reports" />
          </div>
        );
      case 'priority':
        return (
          <DCard title="Unresolved by Priority" editMode={editMode} onRemove={() => removeWidget(id)}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <DonutChart
                data={priorityData}
                size={180}
                onItemClick={(item) => goTickets({ priority: item.name, view: 'active' })}
              />
            </div>
          </DCard>
        );
      case 'assigned':
        return (
          <DCard title="Tickets by Agent" editMode={editMode} onRemove={() => removeWidget(id)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {assignedData.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: '#718096', fontSize: 13 }}>No data</div>}
              {assignedData.map((item, i) => (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => item.label && goTickets({ assignee: item.label })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTickets({ assignee: item.label }); } }}
                  title={`View tickets for ${item.label}`}
                  style={{ cursor: 'pointer', padding: '4px 0', borderRadius: 6 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: '#1A202C', marginBottom: 4 }}>
                    <span>{item.label}</span><span>{item.total ?? item.value}</span>
                  </div>
                  <div style={{ height: 6, background: '#F0F2F5', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(((item.total ?? item.value) / Math.max(...assignedData.map(d => d.total ?? d.value), 1)) * 100, 100)}%`, height: '100%', background: '#3B5BDB', borderRadius: 3 }} />
                  </div>
                </div>
              ))}
            </div>
          </DCard>
        );
      case 'sla':
        return (
          <DCard title="SLA Metrics" editMode={editMode} onRemove={() => removeWidget(id)} onClick={() => navigate('/reports')} hint="View SLA reports">
            <SlaMetricsWidget complianceRate={slaData.complianceRate} />
          </DCard>
        );
      case 'leaderboard':
        return (
          <DCard title="Agent Leaderboard" editMode={editMode} onRemove={() => removeWidget(id)}>
            <LeaderboardWidget data={agentData} onAgentClick={(name) => goTickets({ assignee: name })} />
          </DCard>
        );
      case 'availability':
        return (
          <DCard title="Agent Availability" editMode={editMode} onRemove={() => removeWidget(id)}>
            <AgentAvailabilityWidget agents={agentData} />
          </DCard>
        );
      case 'approvals':
        return (
          <DCard title="Recent Notifications" editMode={editMode} onRemove={() => removeWidget(id)} onClick={() => goTickets({ view: 'active' })} hint="View all tickets">
            <PendingApprovalsWidget
              approvals={approvalsData}
              onItemClick={(a) => {
                if (a.ticketNumber) navigate(`/tickets/${encodeURIComponent(a.ticketNumber)}`);
                else goTickets({ view: 'active' });
              }}
            />
          </DCard>
        );
      case 'trend':
        return (
          <DCard title="Ticket Volume Trend (Last 30 days)" editMode={editMode} onRemove={() => removeWidget(id)}>
            <TrendSparkline data={trendData} />
          </DCard>
        );
      default:
        return null;
    }
  }

  const fullSpanIds  = ['stats', 'trend'];
  const gridWidgets  = visibleWidgets.filter(w => !fullSpanIds.includes(w.id));
  const fullWidgets  = visibleWidgets.filter(w =>  fullSpanIds.includes(w.id));

  return (
    <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', background: T.bg || '#F0F2F5', position: 'relative', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* ── Top action bar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#1A202C' }}>
          Dashboard
          <span style={{ fontSize: 12, fontWeight: 500, color: '#718096', marginLeft: 10 }}>{visibleWidgets.length} widgets active</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowRecentActivity(true)} style={{ padding: '7px 13px', background: '#FFFFFF', border: '1px solid #DDE1E7', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#1A202C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="clock" size={14} /> Activity
          </button>
          <button onClick={() => setShowAnnouncements(true)} style={{ padding: '7px 13px', background: '#FFFFFF', border: '1px solid #DDE1E7', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#1A202C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="note" size={14} /> Announcements
          </button>
          <button
            onClick={() => { setEditMode(e => !e); }}
            style={{ padding: '7px 13px', background: editMode ? '#FEF3C7' : '#FFFFFF', border: `1px solid ${editMode ? '#F59E0B' : '#DDE1E7'}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: editMode ? '#B45309' : '#1A202C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {editMode ? '✕ Exit Edit' : '✏ Edit Layout'}
          </button>
          <button
            onClick={() => setShowCustomise(true)}
            style={{ padding: '7px 13px', background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 12px var(--accent-bg)' }}
          >
            <Icon name="settings" size={13} color="#fff" /> Customise
          </button>
        </div>
      </div>

      {/* ── Full-span widgets (stats, trend) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {fullWidgets.filter(w => w.id === 'stats').map(w => (
          <div key={w.id} style={{ position: 'relative' }}>
            {editMode && (
              <button onClick={() => removeWidget(w.id)} style={{ position: 'absolute', top: -8, right: -8, zIndex: 20, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>✕</button>
            )}
            {renderWidget(w.id)}
          </div>
        ))}

        {/* ── Grid widgets ── */}
        {gridWidgets.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 16,
            gridAutoRows: 'minmax(300px, auto)',
          }}>
            {gridWidgets.map(w => (
              <div key={w.id} style={{ minHeight: 300, display: 'flex', flexDirection: 'column' }}>
                {renderWidget(w.id)}
              </div>
            ))}
          </div>
        )}

        {/* ── Full-span trend ── */}
        {fullWidgets.filter(w => w.id === 'trend').map(w => (
          <div key={w.id} style={{ position: 'relative' }}>
            {editMode && (
              <button onClick={() => removeWidget(w.id)} style={{ position: 'absolute', top: -8, right: -8, zIndex: 20, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>✕</button>
            )}
            {renderWidget(w.id)}
          </div>
        ))}
      </div>

      {/* Empty state */}
      {visibleWidgets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#718096' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', marginBottom: 8 }}>No widgets visible</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>Use Customise to add widgets back</div>
          <button onClick={() => setShowCustomise(true)} style={{ padding: '10px 20px', background: '#3B5BDB', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Open Customise Panel</button>
        </div>
      )}

      {/* ── Customise side panel ── */}
      {showCustomise && (
        <WidgetPanel
          config={widgetConfig}
          onChange={setWidgetConfig}
          onClose={() => setShowCustomise(false)}
        />
      )}

      {showRecentActivity && <RecentActivityModal onClose={() => setShowRecentActivity(false)} />}
      {showAnnouncements  && <AnnouncementsModal  onClose={() => setShowAnnouncements(false)} />}
    </div>
  );
}
